import { css } from "@linaria/core";
import { styled } from "@linaria/react";
import React from "react";
import { usePatch } from "../../../api/useCall";
import t from "../../../i18n";
import useToasts from "@ui/Toast/useToasts";
import DropzoneV2 from "@ui/DropzoneV2";
import ModalForm from "@ui/Modal/Form";
import { convertSourceSnapshotsFileToJSON } from "../../../domain/parser/snapshot";
import type { ConverterErrorType, ConverterResult } from "../../../domain/parser/snapshot/types";
import Result from "./Result";
import type { Project } from "../../../api/useProjects";
import { getSourceParsingErrorMessage } from "../../../domain/parser/snapshot/utils/errors";
import type { ParsingError } from "../../../domain/parser/types";
import { useSettings } from "../../../api/useSettings";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import Checkbox from "@ui/Checkbox";
import { mergeConvertedOutputs } from "../../../domain/parser/snapshot/utils/merge";
import Section from "./Section";
import SourceIdSelector from "./SourceIdSelector";
import TimeZoneSelector from "./TimeZoneSelector";
import { spacings } from "@ui/Theme";
import useQuery, { useRevalidate } from "../../../api/useQuery";
import { DateTime } from "luxon";

export const MAX_SNAPSHOTS_SIZE_BYTES = 20 * 1024 * 1024; // 20 Mo

function convertToDate(
  date: string,
  timezone: string | null,
  fileType: "csv" | "json" | "context" | undefined,
  setFormInError: (message: string) => void,
): Date {
  let dateJS: Date | null = null;
  if (fileType === "json") {
    dateJS = new Date(date); // no shenanigans, this should be machine generated
  } else {
    const parsed = DateTime.fromISO(date, {
      zone: timezone ?? undefined, // defaults to navigator timezone
    });
    if (parsed.isValid) {
      dateJS = parsed.toJSDate();
    } else {
      // yy is two-digit year, interpreted as > 1960 by default (also accepts 4)
      // "dd/MM/yy HH:mm:ss" and "dd/MM/yyyy HH:mm:ss"
      const parsed2 = DateTime.fromFormat(date, "dd/MM/yy HH:mm:ss", {
        zone: timezone ?? undefined, // defaults to navigator timezone
      });
      if (parsed2.isValid) {
        dateJS = parsed2.toJSDate();
      } else {
        // only accepts "yyyy/MM/dd HH:mm:ss"
        const parsed3 = DateTime.fromFormat(date, "yyyy/MM/dd HH:mm:ss", {
          zone: timezone ?? undefined, // defaults to navigator timezone
        });
        if (parsed3.isValid) dateJS = parsed3.toJSDate();
      }
    }
  }

  if (dateJS === null) {
    setFormInError(`invalid date format: ${date}`);
  } else if (isNaN(dateJS.getTime())) {
    setFormInError(`invalid date: ${date}`);
  }

  return dateJS ?? new Date();
}

function PatchSnapshotsModalForm({
  project,
  onRequestClose,
  openCreateProjectModal,
  onSourceUpdated,
}: {
  project: Project | null;
  onRequestClose: () => void;
  onSourceUpdated: ((sourceId: string) => void) | null;
  openCreateProjectModal: (() => void) | null;
}) {
  const projectId = project === null ? null : project.id;
  const settings = useSettings();
  const { addToast } = useToasts();
  const patchSnapshots = usePatch("/sources/snapshots");
  const [allowSnapshotUpdatedOnConflict, setAllowSnapshotUpdatedOnConflict] = React.useState(false);
  const [uploadValid, setUploadValid] = React.useState<boolean>();
  const [dropZoneErrorText, setDropZoneErrorText] = React.useState<string>();
  const [allowUploadVSProjectSourceIdMissmatch, setAllowUploadVSProjectSourceIdMissmatch] =
    React.useState(false);
  const [filenames, setFilenames] = React.useState<string[]>([]);
  const [fileType, setFileType] = React.useState<"csv" | "json" | "context">();
  const [errorDetails, setErrorDetails] = React.useState<ParsingError<ConverterErrorType>[]>([]);
  const [upload, setUpload] = React.useState<ConverterResult | null>(null);
  const [sourceIdInput, setSourceIdInput] = React.useState<string | null>(null);
  const [timezoneInput, setTimeZoneInput] = React.useState<string | null>(null);

  const projectSourceId = project?.sourceId ?? null;
  const uploadSourceId = upload?.sourceId ?? null;
  const contextSourceId = projectSourceId ?? uploadSourceId;
  const sourceId = contextSourceId ?? sourceIdInput;

  const setFormInError = React.useCallback((message: string) => {
    setUploadValid(false);
    setDropZoneErrorText(message);
  }, []);

  const uploadBody = React.useMemo(() => {
    if (upload === null || sourceId === null) return null;
    return {
      ...upload,
      projectId,
      sourceId,
      snapshots: upload.snapshots.map(snapshot => ({
        ...snapshot,
        date: convertToDate(snapshot.date, timezoneInput, fileType, setFormInError),
        acquisitionStartDate: snapshot.acquisitionStartDate
          ? convertToDate(snapshot.acquisitionStartDate, timezoneInput, fileType, setFormInError)
          : undefined,
        acquisitionEndDate: snapshot.acquisitionEndDate
          ? convertToDate(snapshot.acquisitionEndDate, timezoneInput, fileType, setFormInError)
          : undefined,
      })),
    };
  }, [fileType, sourceId, timezoneInput, upload, setFormInError, projectId]);

  const updateOnSnapshotConflict =
    allowSnapshotUpdatedOnConflict && settings.ALLOW_SNAPSHOT_UPDATES_ON_CONFLICT;

  const checkResponse =
    useQuery("/sources/snapshots", {
      method: "PATCH",
      query: { "dry-run": true, updateOnSnapshotConflict },
      disabled: uploadBody === null,
      suspend: false,
      body: uploadBody === null ? emptyUploadBody : uploadBody,
      keepFresh: true,
    })?.body ?? null;
  const revalidate = useRevalidate();

  const thingsToUpdate = checkResponse
    ? checkResponse.conflictingSnapshotDiffs.length +
        checkResponse.newSnapshotsCount +
        checkResponse.mergedSnapshotsCount >
      0
    : null;
  const fatalConflicts = checkResponse
    ? updateOnSnapshotConflict
      ? false
      : checkResponse.conflictingSnapshotDiffs.length > 0
    : null;

  const getFileType = React.useCallback(
    (file: File) =>
      file.type === "application/json"
        ? ("json" as const)
        : file.type === "text/csv"
          ? ("csv" as const)
          : file.type === "application/vnd.ms-excel"
            ? ("csv" as const)
            : file.type === "" && settings.SOURCE_SNAPSHOT_IMPORT_CONTEXT_FILE
              ? ("context" as const)
              : null,
    [settings.SOURCE_SNAPSHOT_IMPORT_CONTEXT_FILE],
  );

  const handleDrop = React.useCallback(
    async (files: File[]) => {
      if (files.length === 0) throw new Error("no dropped files");

      setUpload(null);
      setTimeZoneInput(null);
      setSourceIdInput(null);
      setUploadValid(undefined);
      setFileType(undefined);
      setErrorDetails([]);

      if (new Set(files.map(f => f.type)).size > 1) {
        setFormInError(
          t(
            "dataSource.import.error.multipleFileTypes",
            files.map(f => f.type),
          ),
        );
        return;
      }
      const firstFile = files[0];
      if (!firstFile) throw new Error("no first file");

      const fileType = getFileType(firstFile);
      if (fileType === null) {
        setFormInError(t("dataSource.import.error.fileTypeNotSupported", firstFile.type));
        return;
      }
      setFileType(fileType);
      const fileNames = files.map(file => file.name);
      setFilenames(fileNames);

      const chunks = [];
      for (const file of files) chunks.push(await convertSourceSnapshotsFileToJSON(file, fileType));
      const { result, errors } = mergeConvertedOutputs(chunks);

      if (errors.length > 0 && errors[0]) {
        const message = getSourceParsingErrorMessage(errors[0]);
        setErrorDetails(errors);
        setFormInError(message);
        return;
      }
      const megaBytes = new TextEncoder().encode(JSON.stringify(result)).length / (1024 * 1024);
      if (megaBytes > 20) {
        setFormInError("JSON too big. Max size is 20MB.");
        return;
      }

      setUploadValid(true);
      setUpload(result);
    },
    [getFileType, setFormInError],
  );

  const submittable =
    uploadBody &&
    thingsToUpdate &&
    !fatalConflicts &&
    (projectSourceId === null ||
      uploadSourceId === null ||
      projectSourceId === uploadSourceId ||
      allowUploadVSProjectSourceIdMissmatch) &&
    !patchSnapshots.loading;

  const handleSubmit = React.useCallback(async () => {
    if (!submittable) return false;
    const { body } = await patchSnapshots({
      body: uploadBody,
      query: {
        "dry-run": false,
        updateOnSnapshotConflict,
      },
    });

    await revalidate(
      "/sources/snapshots",
      undefined,
      undefined,
      { sourceId: uploadBody.sourceId },
      "PATCH",
    );

    if (onSourceUpdated) onSourceUpdated(body.sourceId);
    addToast(
      "success",
      <>
        <div>{t("sources.patchModal.action.success.message", { sourceId: body.sourceId })}</div>
        <ActionLink onClick={openCreateProjectModal ?? undefined}>
          {t("sources.patchModal.action.success.action")}
        </ActionLink>
      </>,
    );
    return true;
  }, [
    addToast,
    onSourceUpdated,
    openCreateProjectModal,
    patchSnapshots,
    submittable,
    updateOnSnapshotConflict,
    uploadBody,
    revalidate,
  ]);

  const handleErrorsDownload = React.useCallback(() => {
    const errors = JSON.stringify(errorDetails, null, 4);
    const a = document.createElement("a");
    a.setAttribute("href", URL.createObjectURL(new Blob([errors], { type: "application/json" })));
    a.setAttribute("download", `${new Date().toISOString()}_source_import_errors.json`);
    a.click();
  }, [errorDetails]);

  return (
    <ModalForm
      cancellable
      onRequestClose={onRequestClose}
      onSubmit={submittable ? handleSubmit : false}
      okButtonName={t("modalForm.button.name.import")}
    >
      <DropzoneV2
        accept={settings.SOURCE_SNAPSHOT_IMPORT_CONTEXT_FILE ? undefined : ".json, .csv"}
        downloadErrors={errorDetails.length ? handleErrorsDownload : undefined}
        errorText={dropZoneErrorText}
        isLoading={patchSnapshots.loading}
        multiple
        nameFile={filenames.length === 1 ? filenames[0]! : filenames}
        version={fileType}
        onDrop={handleDrop}
        pathSampleFile="/metroscopia_data.json"
        validation={uploadValid}
        maxSize={MAX_SNAPSHOTS_SIZE_BYTES}
      />

      {upload && uploadValid ? (
        <>
          {contextSourceId === null || fileType === "context" || fileType === "csv" ? (
            <Section
              title={t("dataSource.import.extraInfo.section.title")}
              className={PatchModalSection}
            >
              {contextSourceId === null ? (
                <>
                  <AdditionnalMessage
                    className={WarningMessage}
                    status="WARNING"
                    text={t("dataSource.import.warning.expected_source_id")}
                  />
                  <SourceIdSelector onChange={setSourceIdInput} />
                </>
              ) : null}
              {fileType === "context" || fileType === "csv" ? (
                <>
                  <AdditionnalMessage
                    className={WarningMessage}
                    status="INFO"
                    text={t("dataSource.import.info.timezoneDefault", {
                      projectTimeZone: project !== undefined,
                    })}
                  />
                  <TimeZoneSelector
                    onChange={setTimeZoneInput}
                    defaultValue={project ? project.timezone : undefined}
                  />
                </>
              ) : null}
            </Section>
          ) : null}
          {projectSourceId !== null &&
          uploadSourceId !== null &&
          projectSourceId !== uploadSourceId ? (
            <>
              <AdditionnalMessage
                status="WARNING"
                text={t("dataSource.import.project.source_id_missmatch", {
                  expectedSourceId: projectSourceId,
                  currentSourceId: uploadSourceId,
                })}
              />
              <Checkbox
                checked={allowUploadVSProjectSourceIdMissmatch}
                onChange={e => setAllowUploadVSProjectSourceIdMissmatch(e.target.checked)}
              >
                {t("dataSource.import.project.bypass_source_id_missmatch", projectSourceId)}
              </Checkbox>
            </>
          ) : null}
          {checkResponse === null ? null : (
            <Result
              className={PatchModalSection}
              project={project}
              patchResponse={checkResponse}
              allowSnapshotUpdatedOnConflict={allowSnapshotUpdatedOnConflict}
              setAllowSnapshotUpdatedOnConflict={setAllowSnapshotUpdatedOnConflict}
            />
          )}
        </>
      ) : null}
    </ModalForm>
  );
}

const ActionLink = styled.div`
  text-decoration: underline;
  cursor: pointer;
  font-weight: bold;
`;

const WarningMessage = css`
  margin-bottom: ${spacings.xs};
`;

const PatchModalSection = css`
  margin-top: 12px;
`;

const emptyUploadBody = {
  projectId: null,
  sourceId: "",
  snapshots: [],
};

export default PatchSnapshotsModalForm;
